import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Table, TableColumn } from 'components/_new/Table/Table';
import { Box, BoxSection } from 'components/_new/Box';
import { Loader } from 'components/_new/Loader';
import { statisticsApi } from 'api/statistics';
import { Avatar } from 'components/_new/Avatar';
import { dateFormat } from '../../../../utils/dateFormat';

interface SessionComment {
	id: string;
	sessionId: number;
	pairId: number;
	userDisplayName: string;
	date: Date | string;
	sessionNumber: number;
	comment: string;
	userAvatarUrl?: string;
}

interface SessionCommentsResponse {
	data: SessionComment[];
	pagination: {
		currentPage: number;
		itemsPerPage: number;
		all: number;
		pages: number;
	};
}

interface SessionCommentsTableProps {
	type: 'mentor' | 'mentee';
	programId: number;
	programName: string;
}

export const SessionCommentsTable: React.FC<SessionCommentsTableProps> = ({ type, programId, programName }) => {
	const { t } = useTranslation();
	const [currentPage, setCurrentPage] = useState(1);
	const [sortField, setSortField] = useState('date');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

	const commentType = type === 'mentor' ? 'mentor' : 'mentee';

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleSort = (field: string, order: 'desc' | 'asc' | null) => {
		if (order) {
			setSortField(field);
			setSortOrder(order);
			setCurrentPage(1);
		}
	};

	const {
		data: commentsData,
		isLoading: commentsLoading,
		isFetching: commentsFetching,
	} = useQuery<SessionCommentsResponse>(
		[
			'getSessionComments',
			{ programId, type: commentType, page: currentPage, sort: { field: sortField, order: sortOrder } },
		],
		() =>
			statisticsApi.getSessionComments({
				programId,
				type: commentType,
				page: currentPage,
				sort: { field: sortField, order: sortOrder },
			}),
		{
			enabled: !!programId,
		}
	);

	const columns: TableColumn<SessionComment>[] = [
		{
			label: t('programPanel.statistics.sessionComments.columns.name'),
			name: 'userDisplayName',
			sortable: true,
			body: (comment) => (
				<div className="flex align-items-center">
					<Avatar size="sm" src={comment.userAvatarUrl} className="mr-2" name={comment.userDisplayName} />
					<Link
						to={`/program/${programName}/pair/${comment.pairId}`}
						className="text-primary hover:underline"
					>
						{comment.userDisplayName}
					</Link>
				</div>
			),
		},
		{
			label: t('programPanel.statistics.sessionComments.columns.date'),
			name: 'date',
			sortable: true,
			body: (comment) => (comment.date ? dateFormat(comment.date, 'date') : '-'),
			isDate: true,
		},
		{
			label: t('programPanel.statistics.sessionComments.columns.sessionNumber'),
			name: 'sessionNumber',
			sortable: true,
			body: (comment) => comment.sessionNumber || '-',
		},
		{
			label: t('programPanel.statistics.sessionComments.columns.comment'),
			name: 'comment',
			body: (comment) => comment.comment || '-',
			bodyStyle: { maxWidth: '300px', whiteSpace: 'normal' },
		},
	];

	return (
		<Box variant="white-bordered" className="stats-item">
			<BoxSection>
				<h3 className="text-lg font-semibold mb-3">
					{t(`programPanel.statistics.sessionComments.title.${type}`)}
				</h3>
			</BoxSection>

			<BoxSection>
				{/* eslint-disable-next-line no-nested-ternary */}
				{commentsLoading ? (
					<div className="flex justify-content-center p-4">
						<Loader />
					</div>
				) : !commentsData?.data || commentsData?.data?.length === 0 ? (
					<div className="text-center p-4 text-gray-500">
						{t('programPanel.statistics.sessionComments.noComments')}
					</div>
				) : (
					<Table
						isRefetching={commentsFetching}
						columns={columns}
						data={commentsData?.data || []}
						defaultSort={{ field: sortField, order: sortOrder }}
						onSort={handleSort}
						pagination={{
							current: currentPage,
							itemsPerPage: commentsData?.pagination?.itemsPerPage || 5,
							total: commentsData?.pagination?.all || 0,
							onPageChange: handlePageChange,
						}}
					/>
				)}
			</BoxSection>
		</Box>
	);
};
